import { useEffect, useMemo, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import { FeatureFlag, FeatureFlagKey } from "../../models/featureFlag";
import {
  FEATURE_FLAG_DESCRIPTIONS,
  FEATURE_FLAG_LABELS,
  FEATURE_FLAG_ORDER,
  FEATURE_FLAGS_WITHOUT_ROLLOUT,
} from "../../utils/constants/feature-flag-constants";
import {
  useGetFeatureFlagsByTenantQuery,
  useUpdateFeatureFlagMutation,
} from "../../store/services/feature-flags";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  FormField,
  FormSection,
  FormSections,
  ActiveBadge,
  FormToggle,
  PageHeader,
  StatusBadge,
  TenantGateBanner,
  useTenantGate,
} from "../../components/ui";
import { BenefitsMetricsTenantScopeHeader } from "../Reporting/components/BenefitsMetricsWidgets";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";

type FeatureFlagDraft = {
  enabled: boolean;
  rolloutPct: number;
};

type FeatureFlagRow = FeatureFlag & {
  label: string;
  description: string;
  draft: FeatureFlagDraft;
  isDirty: boolean;
  isSaving: boolean;
};

const sortFlags = (flags: FeatureFlag[]): FeatureFlag[] =>
  [...flags].sort(
    (left, right) =>
      FEATURE_FLAG_ORDER.indexOf(left.flagKey) - FEATURE_FLAG_ORDER.indexOf(right.flagKey)
  );

export const FeatureFlagsConfig = () => {
  const scope = useTenantScope();
  const gate = useTenantGate();
  const selectedTenantId = scope.tenantId;

  const [drafts, setDrafts] = useState<Record<string, FeatureFlagDraft>>({});
  const [savingFlagKey, setSavingFlagKey] = useState<string | null>(null);

  const {
    data: featureFlags,
    isLoading: isLoadingFlags,
  } = useGetFeatureFlagsByTenantQuery(selectedTenantId ?? "", {
    skip: scope.loading || !selectedTenantId,
  });

  const [updateFeatureFlag] = useUpdateFeatureFlagMutation();

  useEffect(() => {
    setDrafts({});
  }, [selectedTenantId]);

  useEffect(() => {
    if (!featureFlags || !selectedTenantId) {
      return;
    }

    if (!featureFlags.every((flag) => flag.tenantId === selectedTenantId)) {
      return;
    }

    setDrafts((currentDrafts) => {
      const nextDrafts: Record<string, FeatureFlagDraft> = {};

      for (const flag of featureFlags) {
        const currentDraft = currentDrafts[flag.flagKey];
        const hasUnsavedChanges =
          currentDraft !== undefined &&
          (currentDraft.enabled !== flag.enabled ||
            currentDraft.rolloutPct !== flag.rolloutPct);

        nextDrafts[flag.flagKey] = hasUnsavedChanges
          ? currentDraft
          : {
              enabled: flag.enabled,
              rolloutPct: flag.rolloutPct,
            };
      }

      return nextDrafts;
    });
  }, [featureFlags, selectedTenantId]);

  const rows: FeatureFlagRow[] = useMemo(() => {
    const sortedFlags = sortFlags(featureFlags ?? []);
    return sortedFlags.map((flag) => {
      const draft = drafts[flag.flagKey] ?? {
        enabled: flag.enabled,
        rolloutPct: flag.rolloutPct,
      };

      return {
        ...flag,
        label: FEATURE_FLAG_LABELS[flag.flagKey as FeatureFlagKey] ?? flag.flagKey,
        description: FEATURE_FLAG_DESCRIPTIONS[flag.flagKey as FeatureFlagKey] ?? "",
        draft,
        isDirty:
          draft.enabled !== flag.enabled || draft.rolloutPct !== flag.rolloutPct,
        isSaving: savingFlagKey === flag.flagKey,
      };
    });
  }, [featureFlags, drafts, savingFlagKey]);

  const updateDraft = (flagKey: string, patch: Partial<FeatureFlagDraft>) => {
    setDrafts((current) => ({
      ...current,
      [flagKey]: {
        enabled: current[flagKey]?.enabled ?? false,
        rolloutPct: current[flagKey]?.rolloutPct ?? 100,
        ...patch,
      },
    }));
  };

  const handleSave = async (row: FeatureFlagRow) => {
    if (!selectedTenantId) {
      return;
    }

    setSavingFlagKey(row.flagKey);
    try {
      await updateFeatureFlag({
        tenantId: selectedTenantId,
        flagKey: row.flagKey,
        enabled: row.draft.enabled,
        rolloutPct: row.draft.rolloutPct,
      }).unwrap();
      toast.success(`${row.label}: configuración guardada`);
    } catch (error) {
      toast.error(
        getAdminFacingApiErrorMessage(error, "No se pudo guardar la configuración.")
      );
    } finally {
      setSavingFlagKey(null);
    }
  };

  const isLoading = scope.loading || (!!selectedTenantId && isLoadingFlags);

  if (isLoading) {
    return (
      <div className="p-card gap-4 p-3">
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (!selectedTenantId) {
    // SYSTEM_DESIGN.md §6.4 — el bloqueo por scope global se muestra con el
    // banner compartido, igual que Beneficios, Eventos y Categorías. Antes esta
    // pantalla era la única del BO con su propio Message y su propio texto.
    return <TenantGateBanner action="activar o desactivar características" gate={gate} />;
  }

  return (
    <div className="p-card feature-flags-page">
      <div className="feature-flags-page__header">
        <PageHeader title="Activación gradual por tenant" />
        <p className="text-center text-color-secondary m-0">
          Activá o desactivá características y definí qué porcentaje de usuarios las
          recibe. Los cambios se aplican en hasta 60 segundos.
        </p>
      </div>

      <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

      {rows.length === 0 ? (
        <p className="text-center text-color-secondary py-4">
          No hay características configuradas para este tenant.
        </p>
      ) : (
        <FormSections key={selectedTenantId}>
          {rows.map((row) => (
            <FormSection
              key={row.flagKey}
              title={row.label}
              actions={
                <span className="flex align-items-center gap-2">
                  {/* El encabezado tiene que poder leerse con la sección
                      cerrada: sin esto había que abrir las 9 para saber cuáles
                      están prendidas. Solo el estado guardado, sin el alcance:
                      el % vive en el control de adentro, y arriba confundía
                      cuando el borrador todavía no estaba guardado. */}
                  {row.isDirty && <StatusBadge tone="pending" label="Sin guardar" />}
                  <ActiveBadge active={row.enabled} gender="f" />
                </span>
              }
            >
              <p className="feature-flags-page__description">{row.description}</p>
              <div className="feature-flags-page__settings">
                <FormToggle label="Activa" htmlFor={`enabled-${row.flagKey}`}>
                  <InputSwitch
                    inputId={`enabled-${row.flagKey}`}
                    checked={row.draft.enabled}
                    onChange={(event) =>
                      updateDraft(row.flagKey, { enabled: Boolean(event.value) })
                    }
                  />
                </FormToggle>

                {!FEATURE_FLAGS_WITHOUT_ROLLOUT.includes(row.flagKey) && (
                  <FormField
                    label="Alcance (%)"
                    htmlFor={`rollout-${row.flagKey}`}
                    className="feature-flags-page__rollout"
                  >
                    <div className="feature-flags-page__percentage">
                      <Slider
                        value={row.draft.rolloutPct}
                        min={0}
                        max={100}
                        step={1}
                        pt={{ handle: { "aria-label": `Alcance (%): ${row.label}` } }}
                        onChange={(event) =>
                          updateDraft(row.flagKey, { rolloutPct: Number(event.value) })
                        }
                        disabled={!row.draft.enabled}
                      />
                      <InputNumber
                        inputId={`rollout-${row.flagKey}`}
                        value={row.draft.rolloutPct}
                        min={0}
                        max={100}
                        suffix="%"
                        onValueChange={(event) =>
                          updateDraft(row.flagKey, { rolloutPct: Number(event.value ?? 0) })
                        }
                        disabled={!row.draft.enabled}
                      />
                    </div>
                  </FormField>
                )}

                <div className="feature-flags-page__actions">
                  <Button
                    label="Guardar cambios"
                    icon="pi pi-check"
                    size="small"
                    disabled={!row.isDirty || row.isSaving}
                    loading={row.isSaving}
                    onClick={() => handleSave(row)}
                  />
                </div>
              </div>
            </FormSection>
          ))}
        </FormSections>
      )}
    </div>
  );
};
